<script setup lang="ts">
import Article from '~/entities/article/ui/Article.vue';
import ArticlePartner from '~/entities/article/ui/ArticlePartner.vue';
import { OG_TYPE } from '../lib/seo';
import {
  OG_SITE_NAME,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  getAbsoluteUrl,
  getOgImageMeta,
} from '~/shared/lib/seo';
import {
  CLIENT_API_EXPERTS_OPINIONS_URL,
  CLIENT_API_NEWS_MATERIALS_URL,
  CLIENT_NEWS_BY_AUTHOR_URL,
} from '~/shared/api/client/news/news.const';
import { buildArticleRequest } from '~/shared/lib/buildArticleRequest';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { customFetch } from '~/shared/lib/customFetch';
import type { MaterialDTO } from '~/shared/api/client/news/news.dto';
import type { ExpertNewsDTO } from '~/shared/api/client/expert-news/expert-news.dto';
import { isMaterialDTO } from '~/shared/lib/isMaterialDTO';
import { isPartnerArticle } from '~/shared/lib/isPartnerArticle';
import OgImage from '~/shared/images/og-image.png';
import { getAuthorName } from '~/shared/lib/getAuthorName';
import { useOrigin } from '~/shared/lib/useOrigin';

definePageMeta({
  scrollToTop: false,
});

const route = useRoute();

const newsId = ref<number | undefined>(
  Number(route.params.id || route.query.id),
);
const requestedNewsId = ref<number | undefined>(newsId.value);

const isPreview = ref<boolean>(Boolean(route.query.hash));
const isFirstLoadArticle = useState<boolean>('isFirstLoadArticle', () => true);

const newsType = ref<string | undefined>(
  isPreview.value ? undefined : route.path.split('/', 2)[1],
);

const isExpertNews = computed(() => newsType.value === 'expert');

const seoTitle = computed(() => {
  return isExpertNews.value ? 'Мнение эксперта' : 'Новость';
});

const request = buildArticleRequest(route, isFirstLoadArticle.value);

if (!request) {
  throw createError({
    status: 404,
    message: 'Материал не найден',
    fatal: true,
  });
}

const data = useState<MaterialDTO[] | ExpertNewsDTO[] | null>(
  'articles-data',
  () => null,
);

// Partner article: its own template without infinite scroll, data always
// holds a single item. useState('is-partner-article'), which the layout uses
// to drop site-wide banners and the side feed, is set in the
// partner-article.global middleware — before the layout renders, otherwise
// SSR HTML would diverge from the client (hydration mismatch).
const isPartnerMaterial = computed(() => isPartnerArticle(data.value?.[0]));

const observer = ref<IntersectionObserver | null>(null);
const observerScroll = ref<IntersectionObserver | null>(null);

const loadingRef = ref<HTMLDivElement | null>(null);
const articleRef = ref<(typeof Article)[] | null>(null);

const isLoading = ref(false);
// const isError = ref(false);

const pendingResponse = ref<Promise<unknown> | null>(null);

const buildArticlePath = (
  articleType: string | null | undefined,
  articleId: number | undefined,
) => {
  if (!articleType || !articleId) {
    return null;
  }

  return `/${articleType}/${articleId}`;
};

const sendMetrikaHit = (path: string) => {
  (
    window as unknown as {
      ym?: (id: number, event: string, name: string) => void;
    }
  ).ym?.(24440456, 'hit', path);
};

const updateSeo = (data: MaterialDTO | ExpertNewsDTO) => {
  const origin = useOrigin();
  const url = import.meta.client
    ? origin + window.location.pathname
    : origin + useRouter().currentRoute.value.fullPath;

  const title = data?.title ?? seoTitle.value;
  const description = isMaterialDTO(data)
    ? data.subject
    : [data.person?.fio, data.person?.position].filter(Boolean).join(', ');
  const image =
    getAbsoluteUrl(origin, data?.image?.photo?.file_url) ??
    getAbsoluteUrl(origin, OgImage);
  const about = data.category?.name ? { name: data.category.name } : undefined;
  const breadcrumbs = data.sectors?.length
    ? data.sectors.map((sector) => ({ id: sector.id, name: sector.name }))
    : undefined;

  const jsonLdGraph = [
    {
      '@type': 'Article',
      headline: title,
      description: description,
      image: image,
      datePublished: data?.publication_date,
      dateModified: data?.updated_datetime,
      about,
      author: {
        '@type': 'Person',
        name: isMaterialDTO(data)
          ? getAuthorName(data?.author)
          : (data as ExpertNewsDTO)?.person?.fio || undefined,
        url:
          isMaterialDTO(data) && data?.author
            ? `${CLIENT_NEWS_BY_AUTHOR_URL}${data?.author?.id}`
            : undefined,
      },
      publisher: {
        '@type': 'Organization',
        name: OG_SITE_NAME,
        logo: {
          '@type': 'ImageObject',
          url: getAbsoluteUrl(origin, OgImage),
          width: OG_IMAGE_WIDTH,
          height: OG_IMAGE_HEIGHT,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
    },
    ...(breadcrumbs?.length
      ? [
          {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((item) => ({
              '@type': 'ListItem',
              position: 1,
              name: item.name,
            })),
          },
        ]
      : []),
  ];

  const jsonLdScripts = [
    {
      type: 'application/ld+json',
      key: 'ld-json',
      innerHTML: {
        '@context': 'https://schema.org',
        '@graph': jsonLdGraph,
      },
    },
  ];

  useHead({
    title: title,
    link: [{ rel: 'canonical', href: url }],
    meta: [
      { name: 'description', content: description },
      { property: 'og:type', content: OG_TYPE },
      { property: 'og:url', content: url },
      {
        property: 'article:published_time',
        content: data?.publication_date,
      },
      {
        property: 'article:modified_time',
        content: data?.updated_datetime,
      },
      { property: 'og:title', content: title },
      {
        property: 'og:description',
        content: description,
      },
      { property: 'og:site_name', content: OG_SITE_NAME },
      ...getOgImageMeta(origin, data?.image?.photo?.file_url),
    ],
    script: jsonLdScripts,
  });
};

// The first article request was already made in the partner-article.global
// middleware (before the layout renders); here the same key is read from the
// payload via getCachedData, so normally there is no network request.
const {
  status,
  data: initialData,
  error,
} = await useCustomFetch<MaterialDTO>(request.url, {
  method: 'GET',
  key: request.key,
  getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
});

if (initialData.value) {
  data.value = isPreview.value
    ? [(initialData.value as unknown as { data: MaterialDTO }).data]
    : [initialData.value];
}

if (error.value) {
  throw createError({
    status: 404,
    message: 'Материал не найден',
    fatal: true,
  });
}

if (initialData.value?.redirect && status.value === 'success') {
  navigateTo(initialData.value.redirect, { redirectCode: 301, external: true });
}

if (
  !isPreview.value &&
  status.value === 'success' &&
  isExpertNews.value &&
  initialData.value?.type &&
  initialData.value.type !== 'expert'
) {
  navigateTo(`/${initialData.value.type}/${initialData.value.id}`, {
    redirectCode: 301,
  });
}

if (initialData.value) {
  updateSeo(initialData.value);
}

onMounted(async () => {
  if (import.meta.client && !isPreview.value) {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    await nextTick();

    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
  }
});

watch(
  () => requestedNewsId.value,
  async (newId) => {
    const currentData = data.value;

    if (
      !newId ||
      !currentData ||
      currentData.length === 0 ||
      currentData.find((item) => item.id === newId)
    )
      return;

    if (pendingResponse.value) {
      await pendingResponse.value;
    }

    isLoading.value = true;

    const lastItem = currentData[currentData.length - 1];
    const isMaterial = isMaterialDTO(lastItem as MaterialDTO);

    const nextItemUrl = isMaterial
      ? CLIENT_API_NEWS_MATERIALS_URL
      : CLIENT_API_EXPERTS_OPINIONS_URL;

    pendingResponse.value = customFetch(`${nextItemUrl}${newId}/`, {
      method: 'GET',
    }).then((result) => {
      pendingResponse.value = null;

      if (isMaterial) {
        const typedResult = result as MaterialDTO;
        const nextId = (lastItem as MaterialDTO).next_news_id;

        data.value =
          nextId === newId
            ? [...(currentData as MaterialDTO[]), typedResult]
            : [typedResult];
      } else {
        const typedResult = result as ExpertNewsDTO;
        const nextId = (lastItem as ExpertNewsDTO).next_expert_id;

        data.value =
          nextId === newId
            ? [...(currentData as ExpertNewsDTO[]), typedResult]
            : [typedResult];
      }
    });
  },
);

watch(
  () => [articleRef.value?.length],
  () => {
    if (articleRef.value && data.value && !isPreview.value) {
      if (observerScroll.value) {
        observerScroll.value.disconnect();
      }

      observerScroll.value = new IntersectionObserver((entries) => {
        entries.forEach(
          (entry) => {
            if (entry.isIntersecting) {
              const nextId = Number(entry.target.getAttribute('data-id'));
              const nextType = entry.target.getAttribute('news-type');
              const nextPath = buildArticlePath(nextType, nextId);

              if (
                nextPath &&
                (nextId !== newsId.value ||
                  window.location.pathname !== nextPath)
              ) {
                window.history.pushState({}, '', nextPath);
                sendMetrikaHit(nextPath);
                newsType.value = nextType || newsType.value;
                newsId.value = nextId;

                if (data.value) {
                  updateSeo(
                    data.value.find((item) => item.id === newsId.value) as
                      | MaterialDTO
                      | ExpertNewsDTO,
                  );
                }
              }
            }
          },
          {
            rootMargin: '0px',
            threshold: 0.2,
          },
        );
      });

      articleRef.value.forEach((article) => {
        if (article.$el) {
          observerScroll.value?.observe(article.$el);
        }
      });
    }
  },
);

watchEffect(() => {
  if (
    import.meta.client &&
    loadingRef.value &&
    !observer.value &&
    !isPreview.value
  ) {
    observer.value = new IntersectionObserver((entries) => {
      entries.forEach(
        (entry) => {
          if (
            entry.isIntersecting &&
            data.value &&
            status.value !== 'pending'
          ) {
            const lastItem = data.value?.[data.value.length - 1];
            const isMaterial = isMaterialDTO(
              lastItem as MaterialDTO | ExpertNewsDTO,
            );
            const nextId = isMaterial
              ? (lastItem as MaterialDTO)?.next_news_id
              : (lastItem as ExpertNewsDTO)?.next_expert_id;

            if (nextId && requestedNewsId.value !== nextId) {
              requestedNewsId.value = nextId;
            }
          }
        },
        {
          rootMargin: '0px',
          threshold: 0.2,
        },
      );
    });

    observer.value.observe(loadingRef.value);
  }
});
</script>
<template>
  <div>
    <template v-if="data && data.length > 0">
      <ArticlePartner v-if="isPartnerMaterial && data[0]" :item="data[0]" />

      <template v-else>
        <Article
          v-for="(item, index) in data ?? []"
          ref="articleRef"
          :key="item.id"
          :data-id="item.id"
          :class="$style.article"
          :item="item"
          :hide-back="index !== 0"
          :hide-banner="true"
          :news-type="
            isMaterialDTO(item) ? (item as MaterialDTO).type : 'expert'
          "
          :index="index"
        />

        <div
          v-if="
            !isPreview &&
            ((data[data.length - 1] as MaterialDTO)?.next_news_id ||
              (data[data.length - 1] as ExpertNewsDTO)?.next_expert_id)
          "
          ref="loadingRef"
        >
          <Loader
            :class="$style.loader"
            style="margin-top: var(--space-2-5x)"
          />
        </div>
      </template>
    </template>
    <div
      v-else-if="
        status === 'error' || (status === 'success' && data && !('id' in data))
      "
      :class="$style.error"
    >
      <p>Не удалось получить новость</p>
      <p>Попробуйте еще раз</p>
    </div>
    <template v-else-if="!data && status === 'pending'">
      <Loader />
    </template>
  </div>
</template>

<style lang="scss" module>
.article {
  padding-bottom: var(--space-2-5x);

  &:first-child {
    padding-top: var(--space-2-5x);
  }

  + .article {
    margin-top: var(--space-2-5x);
  }
}

.error {
  text-align: center;
  padding-top: 20px;
  p {
    margin: 10px 0;
  }
}

@media (max-width: 767px) {
  .article {
    + .article {
      margin-top: var(--space-3x);
    }
  }
}

@media (max-width: 479px) {
  .article {
    &:first-child {
      padding-top: var(--space-2x);
    }
    padding-bottom: var(--space-2x);
  }
}
</style>
